/**
 * ConversionManager - Handles image format conversion
 */
export class ConversionManager {
  private convertedBlob: Blob | null = null;

  /**
   * Convert an image to a different format
   */
  async convert(image: HTMLImageElement, format: string, quality: number = 0.92): Promise<Blob> {
    const normalizedFormat = format.toLowerCase();
    
    // Handle special formats that canvas.toBlob doesn't support universally
    if (normalizedFormat === 'gif') {
      return this.convertToGIF(image);
    } else if (normalizedFormat === 'tiff') {
      return this.convertToTIFF(image);
    } else if (normalizedFormat === 'bmp') {
      return this.convertToBMP(image);
    }
    
    // Standard canvas conversion for PNG, JPEG, WebP
    return new Promise((resolve, reject) => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        // Get context
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw image
        ctx.drawImage(image, 0, 0);

        // Determine MIME type
        const mimeType = this.getMimeType(format);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert image'));
            }
          },
          mimeType,
          quality
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Convert image to GIF format using gifenc
   */
  private async convertToGIF(image: HTMLImageElement): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        // Dynamic import of gifenc
        const { quantize, applyPalette, GIFEncoder } = await import('gifenc');
        
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(image, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, width, height);
        
        // Quantize the colors to create a 256-color palette
        const palette = quantize(imageData.data, 256);
        
        // Apply the palette to get indexed pixels
        const index = applyPalette(imageData.data, palette);
        
        // Create GIF encoder
        const gif = GIFEncoder();
        
        // Write a single frame
        gif.writeFrame(index, width, height, { palette });
        
        // Finish and get buffer
        gif.finish();
        const buffer = gif.bytes();
        
        // Create blob
        const blob = new Blob([buffer], { type: 'image/gif' });
        resolve(blob);
      } catch (error) {
        console.error('GIF conversion error:', error);
        reject(error);
      }
    });
  }

  /**
   * Convert image to TIFF format using UTIF
   */
  private async convertToTIFF(image: HTMLImageElement): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        // Dynamic import of UTIF
        const UTIF = await import('utif');

        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(image, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Prepare RGBA data for UTIF
        const rgba = new Uint8Array(imageData.data);
        
        // Create TIFF using UTIF - properly format the IFD (Image File Directory)
        const tiffData = UTIF.encodeImage(
          rgba.buffer,
          canvas.width,
          canvas.height
        );
        
        // Create blob from TIFF data
        const blob = new Blob([tiffData], { type: 'image/tiff' });
        resolve(blob);
      } catch (error) {
        console.error('TIFF conversion error:', error);
        reject(error);
      }
    });
  }

  /**
   * Convert image to BMP format
   * Note: BMP format encoding manually since bmp-js requires Node.js Buffer
   */
  private async convertToBMP(image: HTMLImageElement): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(image, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const width = canvas.width;
        const height = canvas.height;
        
        // Create BMP file manually
        const extraBytes = (4 - ((width * 3) % 4)) % 4;
        const rgbSize = (width * 3 + extraBytes) * height;
        const headerSize = 54;
        const fileSize = headerSize + rgbSize;
        
        const buffer = new ArrayBuffer(fileSize);
        const view = new DataView(buffer);
        
        // BMP Header
        view.setUint8(0, 0x42); // 'B'
        view.setUint8(1, 0x4D); // 'M'
        view.setUint32(2, fileSize, true);
        view.setUint32(6, 0, true);
        view.setUint32(10, headerSize, true);
        
        // DIB Header
        view.setUint32(14, 40, true); // Header size
        view.setInt32(18, width, true);
        view.setInt32(22, -height, true); // Negative for top-down
        view.setUint16(26, 1, true); // Planes
        view.setUint16(28, 24, true); // Bits per pixel
        view.setUint32(30, 0, true); // Compression
        view.setUint32(34, rgbSize, true);
        view.setUint32(38, 2835, true); // X pixels per meter
        view.setUint32(42, 2835, true); // Y pixels per meter
        view.setUint32(46, 0, true); // Colors in palette
        view.setUint32(50, 0, true); // Important colors
        
        // Pixel data
        let pos = headerSize;
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            view.setUint8(pos++, imageData.data[i + 2]); // B
            view.setUint8(pos++, imageData.data[i + 1]); // G
            view.setUint8(pos++, imageData.data[i]);     // R
          }
          // Add padding
          for (let j = 0; j < extraBytes; j++) {
            view.setUint8(pos++, 0);
          }
        }
        
        // Create blob
        const blob = new Blob([buffer], { type: 'image/bmp' });
        resolve(blob);
      } catch (error) {
        console.error('BMP conversion error:', error);
        reject(error);
      }
    });
  }

  /**
   * Get MIME type for a given format
   */
  private getMimeType(format: string): string {
    const mimeTypes: { [key: string]: string } = {
      'png': 'image/png',
      'jpeg': 'image/jpeg',
      'jpg': 'image/jpeg',
      'webp': 'image/webp',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'tiff': 'image/tiff'
    };

    return mimeTypes[format.toLowerCase()] || 'image/png';
  }

  /**
   * Store the converted blob
   */
  setConvertedBlob(blob: Blob): void {
    this.convertedBlob = blob;
  }

  /**
   * Get the converted blob
   */
  getConvertedBlob(): Blob | null {
    return this.convertedBlob;
  }

  /**
   * Reset the conversion manager
   */
  reset(): void {
    this.convertedBlob = null;
  }

  /**
   * Get estimated file size for a format
   */
  getEstimatedSize(image: HTMLImageElement, format: string): number {
    const pixels = image.naturalWidth * image.naturalHeight;
    
    // Rough estimates based on format
    const estimates: { [key: string]: number } = {
      'png': pixels * 3, // RGB, lossless
      'jpeg': pixels * 0.1, // Lossy compression
      'jpg': pixels * 0.1,
      'webp': pixels * 0.08, // Better compression than JPEG
      'gif': pixels * 0.5, // Limited colors
      'bmp': pixels * 3, // Uncompressed
      'tiff': pixels * 3 // Usually uncompressed
    };

    return estimates[format.toLowerCase()] || pixels;
  }
}
